import { Injectable } from '@angular/core';
import { BsApiService } from '@app/core/services/bs-api/bs-api-service';
import { IObservableStore, BaseObservableStore } from '@app/core/store/infrastructure/base-observable.store';
import { isNullOrUndefined } from 'util';
import { BehaviorSubject } from 'rxjs';
import { ILibrariesState } from './libraries.state';
import { ILibrary } from '../../../model/library/library';
import { ArrayUtilities } from '../../../utilities/array-utilities';
import { RequeueService } from '../../../services/requeue/requeue.service';
import { IRequeueParams } from '../../../bsshapi/api-wrappers';
import { finalize } from 'rxjs/operators';
import { V2LabRequeue } from '@bssh/ng-sdk';
import { ToastrService } from '@bssh/comp-lib';
import { ToastrMessages } from '../../../utilities/toastr-messages';
import { AbstractResourceStore } from '../../resource/abstract.resource.store';
import { ResourceType } from '@app/core/model/resource-type';

// Interface
export interface ILibrariesStore extends IObservableStore<ILibrariesState> {
    updateSelectedLibraries(selections: ILibrary[]): void;
    canRequeueLibrary(): boolean;
    requeueLibrary(requeuParams: IRequeueParams): void;
    clearSelectedLibraries(): void;
}

@Injectable({
    providedIn: 'root'
})
export class LibrariesStore extends AbstractResourceStore<ILibrariesState> implements ILibrariesStore {
    private refreshSubject = new BehaviorSubject<boolean>(false);
    librariesListRefreshed$ = this.refreshSubject.asObservable();

    // pass in the state slices we are interested in...
    constructor(private requeueService: RequeueService, private toastrService: ToastrService) {
        // currently library store supports list only
        // ------------------------------------------>
        super(['selectedLibraries', 'libraryStateError', 'currentBioSample', 'currentUserId']);
    }

    /**
     * Sets state after api call is made to update libraries
     * @param propertyChange Property changed
     * @param updatedLibraries updated libraries
     * @param actionName Action name
     * @param error error
     * @param forceRefresh force refresh
     */
    private setStateOnSuccess(propertyChange: object, updatedLibraries: ILibrary[], actionName: LibraryStoreActions,
                              error: any = null, forceRefresh?: boolean) {

        const { selectedLibraries } = { ...this.getState() };
        let newSelectedLibrariesList = selectedLibraries;

        if (isNullOrUndefined(selectedLibraries) || selectedLibraries.length === 0) {
            return;
        }

        updatedLibraries.forEach(library => {
            newSelectedLibrariesList = this.getUpdatedLibrariesList(library, selectedLibraries, propertyChange);
        });

        this.setState({
            selectedLibraries: newSelectedLibrariesList,
            libraryStateError: error,
        },
            actionName);

        this.refreshSubject.next(forceRefresh);
    }

    private getUpdatedLibrariesList(library: ILibrary, librariesList: ILibrary[], propertyUpdate: object): ILibrary[] {
        if (!isNullOrUndefined(librariesList)) {
            const idx = librariesList.findIndex(l => l.Id === library.Id);
            if (!isNullOrUndefined(idx) && idx !== -1) {
                librariesList[idx] = Object.assign(librariesList[idx], propertyUpdate);
            }
        }
        return librariesList;
    }

    public updateSelectedLibraries(selectedLibraries: ILibrary[]): void {
        this.setState({ ...this.getState(), selectedLibraries }, LibraryStoreActions.UpdateSelectedLibraries);
    }

    // currently library store supports list only
    getActiveList(): ILibrary[] {
        const { selectedLibraries } = this.getState();
        return selectedLibraries;
    }

    canRequeueLibrary(): boolean {
        const activeLibraries = this.getActiveList();

        // only allowed for single selection
        if (!ArrayUtilities.isArrayOfSize(activeLibraries, 1)) {
            return false;
        }

        const { currentUserId } = this.getState();
        return this.requeueService.canRequeueLibrary(activeLibraries[0], currentUserId);
    }

    requeueLibrary(requeueParams: IRequeueParams): void {
        this.loadingSubject.next(true);
        const activeLibraries = this.getActiveList();
        this.subs.sink = this.requeueService.requeueLibrary(activeLibraries[0], requeueParams).pipe(
            finalize(() => this.loadingSubject.next(false))
        ).subscribe({
            next: (response: V2LabRequeue) => {
                this.setStateOnSuccess({}, activeLibraries, LibraryStoreActions.RequestRequeue, null, true);
                this.toastrService.success(ToastrMessages.LAB_REQUEUE_REQUESTED);
            },
            error: error => {
                this.handleError(error, () => ({ ...this.getState(), libraryStateError: error }));
            }
        });
    }

    clearSelectedLibraries(): void {
        const currentState = this.getState();
        if (currentState != null && currentState.selectedLibraries != null) {
            this.setState({selectedLibraries: null, libraryStateError: null}, LibraryStoreActions.ClearSelectedLibraries);
        }
    }

    getResourceType(): ResourceType {
        return ResourceType.LIBRARIES;
    }

}

// The action-names for this store, for logging/tracking.
export enum LibraryStoreActions {
    UpdateSelectedLibraries = 'UPDATE_SELECTED_LIBRARIES',
    RequestRequeue = 'REQUEST_REQUEUE',
    ClearSelectedLibraries = 'CLEAR_SELECTED_LIBRARIES',
}
